import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Edit, ChevronDown, FileText, CreditCard, 
  Package, Receipt, Mail, Phone, MapPin, Building2, 
  User, Calendar, Clock, DollarSign, AlertCircle,
  Plus, ExternalLink, Download, MoreHorizontal, Truck,
  ChevronRight, MessageSquare, History, ShoppingCart
} from 'lucide-react';
import EnhancedAddPartyModal from '../../components/modals/EnhancedAddPartyModal';
import api from '../../services/api';

const SupplierDetailPage = () => {
  const { businessId, supplierId } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showNewTransactionDropdown, setShowNewTransactionDropdown] = useState(false);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const [transactions, setTransactions] = useState({
    invoices: [],
    payments: [],
    orders: [],
    returns: []
  });

  useEffect(() => {
    if (businessId && supplierId) {
      fetchSupplierDetails();
      fetchTransactions();
    }
  }, [businessId, supplierId]);

  const fetchSupplierDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/parties/${supplierId}`);
      setSupplier(response.data);
    } catch (error) {
      console.error('Failed to fetch supplier:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const invoicesRes = await api.get(`/documents?doc_type=PURCHASE_INVOICE&party_id=${supplierId}`);
      const invoicesData = invoicesRes.data;
      setTransactions(prev => ({
        ...prev,
        invoices: invoicesData.documents || invoicesData || []
      }));
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  };

  const handleEditClose = (shouldRefresh) => {
    setShowEditModal(false);
    if (shouldRefresh) {
      fetchSupplierDetails();
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(Math.abs(amount || 0));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatAddress = (address) => {
    if (!address) return '-';
    if (typeof address === 'string') return address;
    // If address is an object, combine the fields
    const parts = [];
    if (address.line1) parts.push(address.line1);
    if (address.line2) parts.push(address.line2);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.pincode) parts.push(address.pincode);
    if (address.country) parts.push(address.country);
    return parts.length > 0 ? parts.join(', ') : '-';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-orange-600 rounded-full animate-spin"></div>
          <p className="text-sm text-slate-500">Loading supplier details...</p>
        </div>
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600">Supplier not found</p>
          <button 
            onClick={() => navigate(`/business/${businessId}/suppliers`)}
            className="mt-4 text-orange-600 hover:text-orange-700 text-sm font-medium"
          >
            ← Back to Suppliers
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'transactions', label: 'Transactions' },
    { id: 'comments', label: 'Comments' },
    { id: 'mails', label: 'Mails' },
    { id: 'statement', label: 'Statement' }
  ];

  return (
    <div className="min-h-screen bg-slate-50 animate-fadeIn">
      {/* Sticky Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Back + Supplier Name */}
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(`/business/${businessId}/suppliers`)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors duration-200"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                    <span className="text-lg font-semibold text-orange-700">
                      {supplier.name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div>
                    <h1 className="text-lg font-semibold text-slate-800">{supplier.name}</h1>
                    <p className="text-sm text-slate-500">Supplier</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              {/* Edit Button */}
              <button
                onClick={() => setShowEditModal(true)}
                className="inline-flex items-center gap-2 px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors text-sm font-medium"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>

              {/* New Transaction Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowNewTransactionDropdown(!showNewTransactionDropdown)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  New Transaction
                  <ChevronDown className="w-4 h-4" />
                </button>
                {showNewTransactionDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-40">
                    <button 
                      onClick={() => { navigate(`/business/${businessId}/purchases/invoices/new?supplier=${supplierId}`); setShowNewTransactionDropdown(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <ShoppingCart className="w-4 h-4" /> Purchase Invoice
                    </button>
                    <button 
                      onClick={() => { navigate(`/business/${businessId}/purchases/payment-out/new?supplier=${supplierId}`); setShowNewTransactionDropdown(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <CreditCard className="w-4 h-4" /> Payment Made
                    </button>
                    <button 
                      onClick={() => { navigate(`/business/${businessId}/purchase-orders/new?supplier=${supplierId}`); setShowNewTransactionDropdown(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <Package className="w-4 h-4" /> Purchase Order
                    </button>
                  </div>
                )}
              </div>

              {/* More Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowMoreDropdown(!showMoreDropdown)}
                  className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <MoreHorizontal className="w-5 h-5" />
                </button>
                {showMoreDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-40">
                    <button 
                      onClick={() => { navigate(`/business/${businessId}/parties/${supplierId}/ledger`); setShowMoreDropdown(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <History className="w-4 h-4" /> View Ledger
                    </button>
                    <button className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                      <Download className="w-4 h-4" /> Download Statement
                    </button>
                    <div className="border-t border-slate-100 my-1"></div>
                    <button className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" /> Mark Inactive
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mt-4 -mb-4">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === tab.id
                    ? 'text-orange-600 bg-slate-50 border-b-2 border-orange-600'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Click outside to close dropdowns */}
      {(showNewTransactionDropdown || showMoreDropdown) && (
        <div 
          className="fixed inset-0 z-20" 
          onClick={() => { setShowNewTransactionDropdown(false); setShowMoreDropdown(false); }}
        />
      )}

      {/* Main Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Master Information */}
            <div className="lg:col-span-2 space-y-4">
              {/* Basic Info Card */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                  <h3 className="text-sm font-semibold text-slate-700">Basic Information</h3>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Supplier Name</p>
                      <p className="text-sm font-medium text-slate-800">{supplier.name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Supplier Type</p>
                      <p className="text-sm font-medium text-slate-800">{supplier.type || 'Business'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">GSTIN</p>
                      <p className="text-sm font-mono text-slate-800">{supplier.gstin || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Status</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        supplier.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {supplier.status || 'Active'}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Payment Terms</p>
                      <p className="text-sm font-medium text-slate-800">{supplier.credit_days || 0} days</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Credit Limit</p>
                      <p className="text-sm font-medium text-slate-800">{formatCurrency(supplier.credit_limit)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Details Card */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                  <h3 className="text-sm font-semibold text-slate-700">Contact Details</h3>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                    <div className="flex items-start gap-3">
                      <Phone className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Mobile</p>
                        <p className="text-sm font-medium text-slate-800">{supplier.mobile || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Mail className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Email</p>
                        <p className="text-sm font-medium text-slate-800">{supplier.email || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <User className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Contact Person</p>
                        <p className="text-sm font-medium text-slate-800">{supplier.contact_person || '-'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Address Card */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                  <h3 className="text-sm font-semibold text-slate-700">Address</h3>
                </div>
                <div className="p-5">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Business Address</p>
                      <p className="text-sm text-slate-800">
                        {formatAddress(supplier.billing_address) !== '-' 
                          ? formatAddress(supplier.billing_address) 
                          : formatAddress(supplier.address) !== '-'
                            ? formatAddress(supplier.address)
                            : `${supplier.city || ''}${supplier.state ? ', ' + supplier.state : ''}${supplier.pincode ? ' - ' + supplier.pincode : ''}` || '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Business Snapshot */}
            <div className="space-y-4">
              {/* Payables Summary */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                  <h3 className="text-sm font-semibold text-slate-700">Payables Summary</h3>
                </div>
                <div className="p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Amount Payable</span>
                    <span className="text-lg font-semibold text-slate-800">
                      {formatCurrency(supplier.current_balance || supplier.balance)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Advance Paid</span>
                    <span className="text-sm font-medium text-emerald-600">
                      {formatCurrency(supplier.advance_paid || 0)}
                    </span>
                  </div>
                  <div className="border-t border-slate-100 pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Last Payment</span>
                      <span className="text-sm text-slate-700">{formatDate(supplier.last_payment_date)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Operational Status */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                  <h3 className="text-sm font-semibold text-slate-700">Operational Status</h3>
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-slate-50">
                    <span className="text-sm text-slate-600">Open Purchase Orders</span>
                    <span className="text-sm font-medium text-slate-800">{supplier.open_orders || 0}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-50">
                    <span className="text-sm text-slate-600">Pending Bills</span>
                    <span className="text-sm font-medium text-slate-800">{supplier.pending_invoices || 0}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-slate-600">Items Supplied</span>
                    <span className="text-sm font-medium text-slate-800">{supplier.items_supplied || 0}</span>
                  </div>
                </div>
              </div>

              {/* What's Next Card */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-100 p-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <ShoppingCart className="w-4 h-4 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">Record a Purchase</p>
                    <p className="text-xs text-slate-500 mt-1">Create a purchase invoice for this supplier</p>
                    <button
                      onClick={() => navigate(`/business/${businessId}/purchases/invoices/new?supplier=${supplierId}`)}
                      className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-orange-600 hover:text-orange-700"
                    >
                      Create Purchase <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'transactions' && (
          <div className="space-y-4">
            {/* Purchase Invoices Section */}
            <TransactionSection 
              title="Purchase Invoices" 
              icon={ShoppingCart}
              items={transactions.invoices}
              onAddNew={() => navigate(`/business/${businessId}/purchases/invoices/new?supplier=${supplierId}`)}
              emptyMessage="No purchase invoices yet"
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              accentColor="orange"
            />
            
            {/* Payments Section */}
            <TransactionSection 
              title="Payments Made" 
              icon={CreditCard}
              items={transactions.payments}
              onAddNew={() => navigate(`/business/${businessId}/purchases/payment-out/new?supplier=${supplierId}`)}
              emptyMessage="No payments recorded"
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              accentColor="orange"
            />

            {/* Purchase Orders Section */}
            <TransactionSection 
              title="Purchase Orders" 
              icon={Package}
              items={transactions.orders}
              onAddNew={() => navigate(`/business/${businessId}/purchase-orders/new?supplier=${supplierId}`)}
              emptyMessage="No orders yet"
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              accentColor="orange"
            />
          </div>
        )}

        {activeTab === 'comments' && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
            <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No comments yet</p>
            <p className="text-sm text-slate-400 mt-1">Add notes and comments about this supplier</p>
            <button className="mt-4 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium">
              Add Comment
            </button>
          </div>
        )}

        {activeTab === 'mails' && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
            <Mail className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No emails yet</p>
            <p className="text-sm text-slate-400 mt-1">Email communication history will appear here</p>
          </div>
        )}

        {activeTab === 'statement' && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
            <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">Account Statement</p>
            <p className="text-sm text-slate-400 mt-1">Generate and download supplier account statement</p>
            <button className="mt-4 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium">
              Generate Statement
            </button>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <EnhancedAddPartyModal
          isOpen={showEditModal}
          onClose={handleEditClose}
          party={supplier}
          partyType="supplier"
          businessId={businessId}
        />
      )}
    </div>
  );
};

// Transaction Section Component
const TransactionSection = ({ title, icon: Icon, items, onAddNew, emptyMessage, formatCurrency, formatDate, accentColor = 'teal' }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-5 py-4 flex items-center justify-between bg-slate-50/50 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-4 h-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
          <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
            {items?.length || 0}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onAddNew(); }}
            className={`text-sm font-medium ${accentColor === 'orange' ? 'text-orange-600 hover:text-orange-700' : 'text-teal-600 hover:text-teal-700'}`}
          >
            + Add New
          </button>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
        </div>
      </button>
      
      {isExpanded && (
        <div className="border-t border-slate-100">
          {!items || items.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-sm text-slate-400">{emptyMessage}</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {items.slice(0, 5).map((item, idx) => (
                <div key={idx} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{item.doc_number || item.invoice_number || `#${idx + 1}`}</p>
                    <p className="text-xs text-slate-500">{formatDate(item.date || item.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-slate-800">{formatCurrency(item.total || item.amount)}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      item.status === 'PAID' || item.status === 'FINAL' ? 'bg-emerald-100 text-emerald-700' :
                      item.status === 'PENDING' || item.status === 'DRAFT' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {item.status}
                    </span>
                  </div>
                </div>
              ))}
              {items.length > 5 && (
                <div className="p-3 text-center">
                  <button className={`text-sm font-medium ${accentColor === 'orange' ? 'text-orange-600 hover:text-orange-700' : 'text-teal-600 hover:text-teal-700'}`}>
                    View All {items.length} {title}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SupplierDetailPage;
